import { useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useCheckboxGroupOptions } from '@alltrails/denali/components/CheckboxGroup';
import { CheckboxFilterConfig } from '@alltrails/denali/components/FilterBar';
import AdminExploreFilters, { AdminExploreFilterLocation, AdminExploreFilterLocationType } from '../../types/AdminExploreFilters';

const labels = defineMessages<AdminExploreFilterLocationType>({
  area: { defaultMessage: 'Park' },
  city: { defaultMessage: 'City' },
  country: { defaultMessage: 'Country' },
  state: { defaultMessage: 'State' }
});

const useAdminLocationFilterConfig = <T extends AdminExploreFilterLocationType>(
  adminExploreFilterLocationType: T,
  locations: AdminExploreFilterLocation<T>[],
  adminExploreFilters: AdminExploreFilters,
  onFiltersChange: (updatedFilters: AdminExploreFilters) => void
): CheckboxFilterConfig => {
  const intl = useIntl();

  const selectedIds = useMemo(
    () => adminExploreFilters[adminExploreFilterLocationType]?.map(({ id }) => id) || [],
    [adminExploreFilterLocationType, adminExploreFilters]
  );
  const { onChange, options, clearOptions, selectedOptions, setOptions } = useCheckboxGroupOptions(
    locations.map(({ id, name }) => ({
      id: String(id),
      selected: selectedIds.includes(id),
      text: name
    }))
  );

  useEffect(() => {
    setOptions(
      locations.map(({ id, name }) => ({
        id: String(id),
        selected: selectedIds.includes(id),
        text: name
      }))
    );
    // Only want to update the options when the list of locations changes
  }, [locations]);

  useEffect(() => {
    const newFilters = { ...adminExploreFilters };
    delete newFilters[adminExploreFilterLocationType];
    if (selectedOptions.length === 0) {
      // Do nothing, act like there is no filter
    } else {
      newFilters[adminExploreFilterLocationType] = selectedOptions.map(option => ({
        type: adminExploreFilterLocationType,
        id: Number(option.id),
        name: String(option.text)
      }));
    }
    onFiltersChange(newFilters);

    // Only want to trigger this the selected value changes
  }, [selectedOptions]);

  return {
    type: 'checkbox',
    id: 'adminExploreFilterLocationType',
    filterChip: {
      onClear: clearOptions,
      selectedItems: selectedOptions?.map(option => option.text),
      unselectedText: intl.formatMessage(labels[adminExploreFilterLocationType])
    },
    checkboxGroup: {
      labelText: intl.formatMessage(labels[adminExploreFilterLocationType]),
      onChange,
      options
    }
  };
};

export default useAdminLocationFilterConfig;
